<template>
  <teleport to="body">
    <div class="cm-fondo" @click.self="cerrar">
      <article class="cm-caja" role="dialog" aria-modal="true" aria-labelledby="cm-el-titulo"
               data-test="modal-eliminar-concepto">
        <header class="vx-panel-cabecera">
          <div>
            <h2 id="cm-el-titulo" class="vx-panel-titulo">Eliminar «{{ concepto.descripcion }}»</h2>
            <div class="vx-ayuda mt-1">Lo cobrado no se toca nunca.</div>
          </div>
          <button class="vx-btn vx-btn-sm" @click="cerrar">Cancelar</button>
        </header>

        <div class="p-3">
          <div v-if="cargando" class="vx-ayuda">
            <span class="spinner-border spinner-border-sm"></span> Calculando…
          </div>

          <template v-else-if="plan">
            <!-- RN-165: qué pasa con cada cuota, con números. -->
            <p class="mb-3" style="font-size: 13.5px" data-test="ce-resumen-eliminacion">
              {{ resumen }}
            </p>

            <div v-if="plan.a_borrar.length" class="cm-lista mb-2">
              <div v-for="d in plan.a_borrar" :key="d.cr_deuda_id"
                   class="d-flex justify-content-between gap-2 py-1">
                <span class="vx-celda-sub">
                  {{ d.periodo }} · {{ d.nombre }}
                  <span v-if="d.vencida" class="vx-estado vx-estado-atencion ms-1">Vencida</span>
                </span>
                <span class="vx-num" style="font-size: 12.5px">
                  {{ formatearMonto(d.monto_total, moneda) }}
                </span>
              </div>
            </div>

            <label class="vx-etiqueta mt-2" for="ce-el-motivo">Motivo (opcional)</label>
            <input id="ce-el-motivo" v-model.trim="motivo" class="vx-campo vx-campo-sm"
                   placeholder="Ej.: copiado del año anterior por error">
          </template>

          <div v-if="error" class="vx-error mt-2" data-test="ce-error-eliminacion">{{ error }}</div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button
              class="vx-btn vx-btn-sm cm-peligro" :disabled="!plan || trabajando"
              data-test="ce-confirmar-eliminacion" @click="confirmar"
            >
              <span v-if="trabajando" class="spinner-border spinner-border-sm"></span>
              {{ plan?.concepto_resultado === 'DESACTIVADO' ? 'Eliminar cuotas y desactivar' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </article>
    </div>
  </teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
// Eliminar un concepto — ZC-F5-004. SPEC_EDICION_DE_CONCEPTOS RN-165.
import { ref, computed, onMounted } from 'vue'
import {
  formatearMonto, previsualizarEliminacionConcepto, confirmarEliminacionConcepto
} from '@/services/cobranzaService'
import { resumenEliminacion } from '@/utils/conceptoAcciones.mjs'

const props = defineProps({
  concepto: { type: Object, required: true },
  moneda: { type: String, default: 'PYG' }
})
const emit = defineEmits(['cerrar', 'eliminado'])

const plan = ref(null)
const cargando = ref(true)
const trabajando = ref(false)
const motivo = ref('')
const error = ref('')

const resumen = computed(() => resumenEliminacion(plan.value))

onMounted(async () => {
  try {
    plan.value = await previsualizarEliminacionConcepto(props.concepto)
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo calcular qué se elimina.'
  } finally {
    cargando.value = false
  }
})

async function confirmar () {
  error.value = ''
  trabajando.value = true
  try {
    emit('eliminado', await confirmarEliminacionConcepto(props.concepto, { motivo: motivo.value }))
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo eliminar el concepto.'
  } finally {
    trabajando.value = false
  }
}

const cerrar = () => emit('cerrar')
</script>

<style scoped>
.cm-fondo {
  position: fixed; inset: 0; z-index: 2100;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.35);
}
.cm-caja {
  width: min(560px, 100%);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: var(--vx-panel);
  border-radius: var(--vx-radio);
  box-shadow: 0 22px 46px rgba(15, 23, 42, 0.2);
}
.cm-lista { max-height: 220px; overflow-y: auto; }
.cm-peligro { color: #ffffff; background: #b91c1c; border-color: #b91c1c; font-weight: 600; }
.cm-peligro:hover:not(:disabled) { background: #991b1b; border-color: #991b1b; }
</style>
